import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import {
  NotificationActionTypeEnum,
  showCustomNotification,
  showError,
  showNotification,
} from '@vk-hr-tek/core/notifications';
import { FlowTreeNode } from '@vk-hr-tek/ui/FlowTree';

import {
  GetEventTypeWorkflowResponse,
  CompanyEventsStat,
  CompanyGroupUsers,
  GetNodeOptionsResponse,
  GetCollectionOptionsResponse,
  SettingsService,
  UpdateNode,
  UpdateValidatorRequest,
  CompanyEventTypesCollectionOptionsParams,
  DeleteValidatorParams,
  DeleteValidatorRequest,
  CompanyEventTypesCollectionParams,
  UpdateCollectionsRequest,
} from '@app/gen/settings';
import { ThunkExtra } from '@app/store';
import { Entity } from '@app/types';

import { SettingsWorkerWorkflowMapper } from '../../../mappers';
import { SettingsState, SettingsWithRootState } from '../../settings.state';
import {
  ActionEventTypeDto,
  UpdateEventTypeDisabledDto,
  UpdateValidatorDto,
} from '../../../dto';
import { getCompanies } from '../companies';

interface DispatchResult<T> {
  type: string;
  payload: AppError | T | undefined;
}

export const awaitDispatch = async <T>(
  promise: Promise<DispatchResult<T>>,
): Promise<T> => {
  const { type, payload } = await promise;

  if (type.includes('rejected')) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw payload;
  }

  return payload as T;
};

export const getEventType = createAsyncThunk<
  GetEventTypeWorkflowResponse & {
    companyName: string;
    treeData: FlowTreeNode;
  },
  ActionEventTypeDto,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getEventType',
  async (
    actionEventTypeDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      let companies = getState().settings?.companies;

      if (!companies?.length) {
        companies = await awaitDispatch(dispatch(getCompanies()));
      }

      const companyName =
        companies.find((i) => i.id === actionEventTypeDto.companyId)?.name ||
        '';

      if (!companyName) {
        throw new AppError('client', {
          code: 400,
          message: 'компании с таким id не существует',
        });
      }

      const eventTypeService = inject(SettingsService);
      const workflowService = inject(SettingsWorkerWorkflowMapper);
      const result = await eventTypeService.getEventTypeWorkflow({
        params: { event_type_id: actionEventTypeDto.eventTypeId },
      });
      const treeData = await workflowService.transform(result.workflow);
      return { ...result, companyName, treeData };
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateEventTypeDisabled = createAsyncThunk<
  unknown,
  UpdateEventTypeDisabledDto,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateEventTypeDisabled',
  async (
    { eventTypeId, disabled, eventTypeName },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const eventTypeService = inject(SettingsService);
      await eventTypeService.updateEventTypeDisabled({
        params: { event_type_id: eventTypeId },
        body: { disabled },
      });

      dispatch(
        showCustomNotification([
          {
            title: `Тип заявки ${
              disabled ? 'скрыт' : 'виден'
            } в списке доступных при создании новой заявки`,
            message: eventTypeName,
            type: 'info',
          },
        ]),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            title: `Не удалось ${
              disabled ? 'выключить' : 'включить'
            } видимый тип заявки. Попробуйте, пожалуйста, еще раз`,
            message: eventTypeName,
            type: 'error',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getEventTypeCollections = createAsyncThunk<
  GetCollectionOptionsResponse,
  CompanyEventTypesCollectionOptionsParams,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getEventTypeCollections',
  async ({ event_type_id }, { rejectWithValue, extra: { inject } }) => {
    try {
      const eventTypeService = inject(SettingsService);
      const result = await eventTypeService.companyEventTypesCollectionOptions({
        params: { event_type_id },
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateEventTypeCollections = createAsyncThunk<
  unknown,
  CompanyEventTypesCollectionParams &
    UpdateCollectionsRequest & { company_id: string },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateEventTypeCollections',
  async (
    { event_type_id, collection_ids, company_id },
    { dispatch, extra: { inject } },
  ) => {
    try {
      const eventTypeService = inject(SettingsService);
      await eventTypeService.companyEventTypesCollection({
        params: { event_type_id },
        body: { collection_ids },
      });
      dispatch(
        getEventType({
          eventTypeId: event_type_id,
          companyId: company_id,
        }),
      );

      dispatch(
        showCustomNotification([
          {
            message: 'Коллекция успешно изменена!',
            type: 'info',
          },
        ]),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            title: 'Не удалось изменить коллекцию',
            message: 'Попробуйте еще раз',
            type: 'error',
          },
        ]),
      );
    }
  },
);

export const deleteValidator = createAsyncThunk<
  void,
  DeleteValidatorParams & DeleteValidatorRequest & { company_id: string },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/deleteValidator',
  async (
    { event_type_id, validator_id, company_id },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const eventTypeService = inject(SettingsService);
      await eventTypeService.deleteValidator({
        params: { event_type_id },
        body: { validator_id },
      });
      dispatch(showNotification('Валидатор был удален'));
      dispatch(
        getEventType({ eventTypeId: event_type_id, companyId: company_id }),
      );
    } catch (err) {
      dispatch(showError('При удалении валидатора произошла ошибка'));
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateValidator = createAsyncThunk<
  void,
  {
    values: UpdateValidatorDto;
    actions: {
      resolve: (value: unknown) => void;
    };
    companyId: string;
  },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateValidator',
  async (
    { values, actions, companyId },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const eventTypeService = inject(SettingsService);

      const {
        eventTypeId,
        type,
        working_days_only: workingDaysOnly,
        days_count: daysCount,
        validator_id: validatorId,
        operation,
      } = values;

      let daysCountType = 'days_count';

      if (type === 'min_duration' || type === 'max_duration') {
        daysCountType = type;
      }

      await eventTypeService.updateValidator({
        params: { event_type_id: eventTypeId },
        body: {
          validator_id: validatorId,
          settings: {
            type,
            ...(type === 'min_hiring_day'
              ? { operation }
              : {
                  [daysCountType]: daysCount,
                  working_days_only: workingDaysOnly,
                }),
          } as UpdateValidatorRequest['settings'],
        },
      });
      actions.resolve(null);
      dispatch(showNotification('Валидатор был изменен'));
      dispatch(getEventType({ eventTypeId: values.eventTypeId, companyId }));
    } catch (err) {
      actions.resolve({ [FORM_ERROR]: err });
      dispatch(showError('При редактировании валидатора произошла ошибка'));
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateFlowNodes = createAsyncThunk<
  void,
  {
    eventTypeId: string;
    nodes: UpdateNode[];
    actions: {
      resolve: (value: unknown) => void;
      reject: (value: unknown) => void;
    };
  },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateFlowNodes',
  async (
    { eventTypeId, nodes, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    const hasTypeUpdate = nodes.some((node) => 'manager_type' in node);

    try {
      const service = inject(SettingsService);

      await service.updateNodes({
        params: {
          event_type_id: eventTypeId,
        },
        body: {
          nodes,
        },
      });
      if (hasTypeUpdate) {
        dispatch(
          showCustomNotification([
            {
              message: 'Тип оргструктуры изменен',
              type: 'info',
              action: {
                actionType: NotificationActionTypeEnum.Redirect,
                text: 'Настроить импорт оргструктуры из 1С',
                link: '/company/settings/company',
              },
            },
          ]),
        );
      }
      dispatch(showNotification('Данные этапов были изменены'));
      actions.resolve(null);
    } catch (err) {
      actions.reject(err);
      if (hasTypeUpdate) {
        dispatch(
          showError(
            'Не удалось изменить тип оргструктуры!\nПопробуйте еще раз позже',
          ),
        );
      }
      dispatch(
        showError(
          'Не удалось изменить данные этапов!\nПопробуйте еще раз позже',
        ),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getEventTypeNodeOptions = createAsyncThunk<
  GetNodeOptionsResponse,
  string,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getEventTypeNodeOptions',
  async (id, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(SettingsService);
      const res = await service.getNodeOptions({
        params: { event_type_id: id },
      });

      return res;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsEventTypeReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getEventType.pending, (state) => {
    state.eventType.status = 'loading';
    state.eventType.error = null;
    state.eventType.item = null;
  });
  builder.addCase(getEventType.fulfilled, (state, { payload }) => {
    state.eventType.status = 'complete';
    state.eventType.error = null;
    state.eventType.item = payload;
  });
  builder.addCase(getEventType.rejected, (state, { payload, error }) => {
    state.eventType.status = 'failed';
    state.eventType.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
    state.eventType.item = null;
  });

  builder.addCase(updateEventTypeDisabled.fulfilled, (state, { meta }) => {
    if (state.eventType.item) {
      state.eventType.item.event_type.disabled = meta.arg.disabled;
    }
  });

  builder.addCase(getEventTypeNodeOptions.pending, (state) => {
    state.eventType.options = null;
    state.eventType.optionsError = null;
  });
  builder.addCase(getEventTypeNodeOptions.fulfilled, (state, { payload }) => {
    state.eventType.options = payload;
    state.eventType.optionsError = null;
  });
  builder.addCase(
    getEventTypeNodeOptions.rejected,
    (state, { payload, error }) => {
      state.eventType.options = null;
      state.eventType.optionsError =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
      state.eventType.status = 'failed';
    },
  );
  builder.addCase(getEventTypeCollections.pending, (state) => {
    state.eventTypeCollections.items = null;
    state.eventTypeCollections.error = null;
    state.eventTypeCollections.status = 'loading';
  });
  builder.addCase(getEventTypeCollections.fulfilled, (state, { payload }) => {
    state.eventTypeCollections.items = payload.collections;
    state.eventTypeCollections.error = null;
    state.eventTypeCollections.status = 'complete';
  });
  builder.addCase(
    getEventTypeCollections.rejected,
    (state, { payload, error }) => {
      state.eventTypeCollections.items = null;
      state.eventTypeCollections.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
      state.eventTypeCollections.status = 'failed';
    },
  );
};
